import { useCallback, useMemo } from "react";
import { t } from "i18next";

import MapControlLegend from "@/components/map/map-control-legend";
import MapControlTabs from "@/components/map/map-control-tabs";
import { useMapControlLegendState } from "@/components/map/use-map-control-legend-state";
import Icon from "@/components/icon/icon";
import { MapLayersController } from "@/lib/map-layers";
import EnumControl from "@/features/shared/widgets/map/controls/enum-control";
import LayersPanel, {
  type LayerStatusOverride,
  stationLayerParameterErrorOverride,
} from "@/features/shared/widgets/map/controls/layers-panel";
import { ConfigPanelStack } from "@/features/shared/widgets/map/map.styles";
import {
  ModelMapStaticOverlaysPanel,
  type StaticOverlayUiById,
} from "@/features/shared/widgets/model-map/model-map-static-overlays-panel";
import layerControlStyles from "@/features/shared/widgets/model-map/model-map-layer-control.module.css";
import type { StaticMapOverlayEntry } from "@/features/shared/widgets/map/static-overlays/use-static-map-overlay-entries";
import { observedStationMarkersLayerControllerId } from "@/features/shared/widgets/observations-map/observed-station-markers-layer";
import type { MarkerSeriesAggregateStatus } from "@/features/shared/widgets/observations-map/marker-display-state";

type ObservationsMapLayerControlProps = {
  mapLayers: MapLayersController;
  staticOverlayEntries: StaticMapOverlayEntry[];
  staticOverlayUiById: StaticOverlayUiById;
  onSetStaticOverlayVisible: (instanceId: string, visible: boolean) => void;
  stationLayerAggregateStatus?: MarkerSeriesAggregateStatus;
  /**
   * Number of placeholder (ghost) rows to show in the layer list for configured
   * layers whose data is still loading. Already debounced by the caller.
   */
  ghostRowCount?: number;
};

export function ObservationsMapLayerControl({
  mapLayers,
  staticOverlayEntries,
  staticOverlayUiById,
  onSetStaticOverlayVisible,
  stationLayerAggregateStatus,
  ghostRowCount = 0,
}: ObservationsMapLayerControlProps) {
  const {
    activePanel,
    setActivePanel,
    selectedLegendLayerId,
    setSelectedLegendLayerId,
    hasLayersWithLegend,
    openLegend,
  } = useMapControlLegendState(mapLayers.layers);

  const handleShowLegendForLayer = useCallback(
    (layerId: string) => openLegend(layerId),
    [openLegend],
  );

  const hasMapLayers = mapLayers.layers.length > 0;
  const showLayersPanel = hasMapLayers || ghostRowCount > 0;

  const layerStatusOverrides = useMemo(():
    | Record<string, LayerStatusOverride>
    | undefined => {
    if (!stationLayerAggregateStatus) return undefined;

    const override: LayerStatusOverride = {
      isLoading: stationLayerAggregateStatus.anyPending,
    };
    if (stationLayerAggregateStatus.anyError) {
      override.error = stationLayerParameterErrorOverride().error;
    }

    return { [observedStationMarkersLayerControllerId]: override };
  }, [stationLayerAggregateStatus]);

  return (
    <MapControlTabs.Root value={activePanel} onValueChange={setActivePanel}>
      <MapControlTabs.List>
        <MapControlTabs.Trigger value="config" aria-label={t("map.config_tab_label")}>
          <Icon name="layers" size={20} />
        </MapControlTabs.Trigger>
        {hasLayersWithLegend && (
          <MapControlTabs.Trigger value="legend" aria-label={t("map.legend_tab_label")}>
            <Icon name="info" size={20} />
          </MapControlTabs.Trigger>
        )}
      </MapControlTabs.List>

      <MapControlTabs.Content value="config">
        <div className={layerControlStyles.configColumn}>
          {showLayersPanel && (
            <ConfigPanelStack>
              <LayersPanel
                mapLayersController={mapLayers}
                onShowLegendForLayer={handleShowLegendForLayer}
                layerStatusOverrides={layerStatusOverrides}
                ghostRowCount={ghostRowCount}
              />
            </ConfigPanelStack>
          )}
          <ModelMapStaticOverlaysPanel
            entries={staticOverlayEntries}
            uiById={staticOverlayUiById}
            onSetVisible={onSetStaticOverlayVisible}
          />
          {/* Keep dimension pickers last so their dropdowns are not covered by, and do
              not push down, the static overlay toggles. */}
          <EnumControl {...mapLayers} />
        </div>
      </MapControlTabs.Content>
      {hasLayersWithLegend && (
        <MapControlTabs.Content value="legend">
          <MapControlLegend
            layers={mapLayers.layers}
            selectedLegendLayerId={selectedLegendLayerId}
            onSelectedLegendLayerIdChange={setSelectedLegendLayerId}
          />
        </MapControlTabs.Content>
      )}
    </MapControlTabs.Root>
  );
}
